import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Gift, Plus, Ban, History } from 'lucide-react'
import api from '../api'
import { PageHeader, Badge, EmptyState, Table, THead, td, Field, Button, Alert, Modal, ModalHeader, Input, Select, Textarea } from '../components/ui'

// Bonus/Diskon Pelanggan (2026-09-29) -- menu sendiri (pola "1 konsep = 1 menu"). Aturan hitung ada
// di server/bonusShared.js: diskon dari harga paket saja, SEKALI terpakai di 1 tagihan berikutnya,
// SELAMANYA tiap tagihan sampai dihentikan, maks 1 SELAMANYA + 1 SEKALI aktif per pelanggan.

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }
function hariIni() { return new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 10) }
function tambahBulan(iso, n) {
  const [y, m, d] = iso.split('-').map(Number)
  const hasil = new Date(Date.UTC(y, m - 1 + n, 1))
  const hariTerakhir = new Date(Date.UTC(hasil.getUTCFullYear(), hasil.getUTCMonth() + 1, 0)).getUTCDate()
  hasil.setUTCDate(Math.min(d, hariTerakhir))
  return hasil.toISOString().slice(0, 10)
}

const STATUS_TONE = { AKTIF: 'success', TERPAKAI: 'neutral', KEDALUWARSA: 'warning', DIHENTIKAN: 'danger' }
const STATUS_LABEL = { AKTIF: 'Aktif', TERPAKAI: 'Terpakai', KEDALUWARSA: 'Kedaluwarsa', DIHENTIKAN: 'Dihentikan' }

function formKosong(username = '') {
  return { ppp_username: username, jenis: 'PERSEN', nilai: '', masa: 'SEKALI', berlaku_sampai: tambahBulan(hariIni(), 1), alasan: '' }
}

// Cari pelanggan lewat API pencarian yg sudah ada (server-side, 350+ pelanggan -- tidak dimuat semua).
function PilihPelanggan({ value, onChange }) {
  const [q, setQ] = useState(value)
  const [hasil, setHasil] = useState([])
  const [buka, setBuka] = useState(false)

  useEffect(() => {
    if (!buka || q.trim().length < 2) { setHasil([]); return }
    const h = setTimeout(() => {
      api.get('/pelanggan-kontak', { params: { search: q.trim(), limit: 8 } })
        .then(r => setHasil(r.data.items)).catch(() => setHasil([]))
    }, 250)
    return () => clearTimeout(h)
  }, [q, buka])

  return (
    <div style={{ position: 'relative' }}>
      <Input value={q} placeholder="Ketik nama / username / no HP"
        onChange={e => { setQ(e.target.value); setBuka(true); onChange('') }} />
      {buka && hasil.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 10, left: 0, right: 0, top: '100%', marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 260, overflowY: 'auto' }}>
          {hasil.map(p => (
            <button key={p.ppp_username} type="button"
              onClick={() => { onChange(p.ppp_username); setQ(`${p.nama || p.ppp_username} (${p.ppp_username})`); setBuka(false) }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: 'none', cursor: 'pointer', borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{p.nama || p.ppp_username}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{p.ppp_username} · {p.nama_paket || 'tanpa paket'} · {p.status}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function BonusPelanggan() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterUser = searchParams.get('ppp_username') || ''
  const [filterStatus, setFilterStatus] = useState('AKTIF')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(formKosong())
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [info, setInfo] = useState('')

  const [riwayat, setRiwayat] = useState(null)

  useEffect(() => { load() }, [filterStatus, filterUser])
  // Dari tombol "Beri Bonus" di Daftar Pelanggan (?beri=username) -- langsung buka form terisi.
  useEffect(() => {
    const beri = searchParams.get('beri')
    if (beri) {
      setForm(formKosong(beri)); setError(''); setModal(true)
      setSearchParams(filterUser ? { ppp_username: filterUser } : {})
    }
  }, [])

  async function load() {
    setLoading(true)
    try {
      const params = {}
      if (filterStatus) params.status = filterStatus
      if (filterUser) params.ppp_username = filterUser
      const res = await api.get('/bonus-pelanggan', { params })
      setData(res.data)
    } finally { setLoading(false) }
  }

  async function simpan() {
    if (!form.ppp_username) return setError('Pilih pelanggan dari daftar pencarian')
    if (!form.nilai || Number(form.nilai) <= 0) return setError('Nilai diskon harus lebih dari 0')
    if (form.jenis === 'PERSEN' && Number(form.nilai) > 100) return setError('Diskon persen maksimal 100%')
    setSaving(true)
    try {
      const res = await api.post('/bonus-pelanggan', form)
      setModal(false)
      setInfo(res.data.message)
      load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    } finally { setSaving(false) }
  }

  async function hentikan(row) {
    if (!window.confirm(`Hentikan "${row.label}" untuk ${row.nama_pelanggan || row.ppp_username}?\n\nTagihan yang sudah terlanjur dibuat tidak berubah.`)) return
    try {
      await api.post(`/bonus-pelanggan/${row.id}/hentikan`)
      load()
    } catch (e) { alert(e.response?.data?.message || 'Gagal menghentikan') }
  }

  async function lihatRiwayat(row) {
    const res = await api.get(`/bonus-pelanggan/${row.id}/pemakaian`)
    setRiwayat({ bonus: row, items: res.data })
  }

  const contohHarga = 150000
  const contohPotongan = form.jenis === 'PERSEN' ? Math.round(contohHarga * (Number(form.nilai) || 0) / 100) : Math.min(Number(form.nilai) || 0, contohHarga)

  return (
    <div>
      <PageHeader
        title="Bonus Pelanggan"
        subtitle="Diskon tagihan untuk pelanggan tertentu. Dipotong otomatis saat tagihan bulanan dibuat, dihitung dari harga paket (tunggakan tidak ikut didiskon)."
        actions={<Button icon={Plus} onClick={() => { setForm(formKosong(filterUser)); setError(''); setModal(true) }}>Beri Bonus</Button>}
      />

      {info && <Alert type="success">{info}</Alert>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="AKTIF">Aktif</option>
          <option value="TERPAKAI">Terpakai</option>
          <option value="KEDALUWARSA">Kedaluwarsa</option>
          <option value="DIHENTIKAN">Dihentikan</option>
          <option value="">Semua status</option>
        </Select>
        {filterUser && (
          <Badge tone="brand">
            Pelanggan: {filterUser}{' '}
            <button onClick={() => setSearchParams({})} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700 }}>×</button>
          </Badge>
        )}
      </div>

      <Table>
        <THead cols={['Pelanggan', 'Diskon', 'Masa Aktif', 'Status', 'Dipakai', 'Alasan', '']} />
        <tbody>
          {loading ? (
            <tr><td colSpan={7}><EmptyState icon={Gift} title="Memuat..." /></td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={7}><EmptyState icon={Gift} title="Belum ada bonus" hint="Klik Beri Bonus untuk memberi diskon ke pelanggan" /></td></tr>
          ) : data.map(row => (
            <tr key={row.id}>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{row.nama_pelanggan || row.ppp_username}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{row.ppp_username}</div>
              </td>
              <td style={{ ...td, fontWeight: 700 }}>{row.jenis === 'PERSEN' ? `${row.nilai}%` : rp(row.nilai)}</td>
              <td style={td}>
                {row.masa === 'SELAMANYA'
                  ? <Badge tone="brand">⭐ Selamanya</Badge>
                  : <span style={{ fontSize: 12.5 }}>Sekali pakai<br /><span style={{ color: 'var(--text-muted)' }}>s/d {row.berlaku_sampai}</span></span>}
              </td>
              <td style={td}><Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge></td>
              <td style={{ ...td, fontSize: 12.5 }}>
                {row.jumlah_pemakaian > 0
                  ? <button onClick={() => lihatRiwayat(row)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brand)', padding: 0, textAlign: 'left' }}>
                      {row.jumlah_pemakaian} tagihan<br />−{rp(row.total_potongan)}
                    </button>
                  : <span style={{ color: 'var(--text-faint)' }}>Belum</span>}
              </td>
              <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12.5 }}>
                {row.alasan || '-'}
                <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>oleh {row.dibuat_oleh || '-'}, {String(row.created_at || '').slice(0, 10)}</div>
              </td>
              <td style={td}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {row.jumlah_pemakaian > 0 && (
                    <button onClick={() => lihatRiwayat(row)} title="Riwayat pemakaian" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><History size={15} /></button>
                  )}
                  {row.status === 'AKTIF' && (
                    <button onClick={() => hentikan(row)} title="Hentikan" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}><Ban size={15} /></button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {modal && (
        <Modal onClose={() => setModal(false)} width={460}>
          <ModalHeader title="Beri Bonus / Diskon" subtitle="Berlaku mulai tagihan berikutnya" onClose={() => setModal(false)} />
          {error && <Alert type="error">{error}</Alert>}
          <Field label="Pelanggan *" style={{ marginBottom: 14 }}>
            <PilihPelanggan value={form.ppp_username} onChange={u => setForm(f => ({ ...f, ppp_username: u }))} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <Field label="Jenis *">
              <Select value={form.jenis} onChange={e => setForm({ ...form, jenis: e.target.value })}>
                <option value="PERSEN">Persen (%)</option>
                <option value="NOMINAL">Potongan Rupiah</option>
              </Select>
            </Field>
            <Field label={form.jenis === 'PERSEN' ? 'Diskon (%) *' : 'Potongan (Rp) *'}>
              <Input type="number" min="1" max={form.jenis === 'PERSEN' ? 100 : undefined} value={form.nilai}
                onChange={e => setForm({ ...form, nilai: e.target.value })} placeholder={form.jenis === 'PERSEN' ? '10' : '20000'} />
            </Field>
          </div>
          <Field label="Masa Aktif *" style={{ marginBottom: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[['SEKALI', 'Sekali pakai', '1 tagihan berikutnya'], ['SELAMANYA', 'Selamanya', 'Tiap bulan sampai dihentikan']].map(([v, judul, ket]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, masa: v })}
                  style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left', color: 'var(--text)',
                    border: `2px solid ${form.masa === v ? 'var(--brand)' : 'var(--border)'}`, background: form.masa === v ? 'var(--brand-soft)' : 'transparent' }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{v === 'SELAMANYA' ? '⭐ ' : ''}{judul}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{ket}</div>
                </button>
              ))}
            </div>
          </Field>
          {form.masa === 'SEKALI' && (
            <Field label="Berlaku sampai *" style={{ marginBottom: 14 }}>
              <Input type="date" min={hariIni()} value={form.berlaku_sampai} onChange={e => setForm({ ...form, berlaku_sampai: e.target.value })} />
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>Dipakai di tagihan pertama yang dibuat sebelum tanggal ini. Lewat tanggal tanpa terpakai = kedaluwarsa.</div>
            </Field>
          )}
          <Field label="Alasan / keterangan" style={{ marginBottom: 10 }}>
            <Textarea rows={2} value={form.alasan} onChange={e => setForm({ ...form, alasan: e.target.value })} placeholder="mis. Pelanggan setia, kompensasi gangguan 3 hari" />
          </Field>
          {Number(form.nilai) > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg)', borderRadius: 8, padding: '8px 10px' }}>
              Contoh paket {rp(contohHarga)} → potongan {rp(contohPotongan)}, tagihan jadi {rp(contohHarga - contohPotongan)}
              {form.masa === 'SELAMANYA' ? ' setiap bulan.' : ' (sekali).'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={saving} onClick={simpan} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}

      {riwayat && (
        <Modal onClose={() => setRiwayat(null)} width={460}>
          <ModalHeader title="Riwayat Pemakaian" subtitle={`${riwayat.bonus.nama_pelanggan || riwayat.bonus.ppp_username} — ${riwayat.bonus.label}`} onClose={() => setRiwayat(null)} />
          <Table>
            <THead cols={['Periode', 'Harga Paket', 'Potongan']} />
            <tbody>
              {riwayat.items.map(it => (
                <tr key={it.tagihan_id}>
                  <td style={td}>{it.periode}</td>
                  <td style={td}>{rp(it.harga_bulanan)}</td>
                  <td style={{ ...td, color: 'var(--success)', fontWeight: 600 }}>−{rp(it.jumlah)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal>
      )}
    </div>
  )
}
